import { OperationVariables, TypedDocumentNode } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import type { useQuery as ApolloUseQuery } from '@apollo/client/react'
import type { NoInfer } from '@apollo/client/utilities/internal'
import { DocumentNode } from 'graphql'
import { useCallback, useState } from 'react'
import { getPageInfo } from '../utils/getPageInfo'
import { PaginatedPropertyKey, PaginatedQueryVariables } from '../typings'
import { PaginatedQueryOptions, PaginatedQueryResult } from '.'

export function usePaginatedQuery<
  TData extends object,
  TVariables extends PaginatedQueryVariables = OperationVariables,
  TProperty extends PaginatedPropertyKey<TData> = PaginatedPropertyKey<TData>
>(
  query: TypedDocumentNode<TData, TVariables>,
  options: PaginatedQueryOptions<TData, TVariables, TProperty>
): PaginatedQueryResult<TData, TVariables>

export function usePaginatedQuery<
  TData extends object,
  TVariables extends PaginatedQueryVariables = OperationVariables,
  TProperty extends PaginatedPropertyKey<TData> = PaginatedPropertyKey<TData>
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: PaginatedQueryOptions<TData, TVariables, TProperty>
): PaginatedQueryResult<TData, TVariables> {
  const { property, ...queryOptions } = options
  const queryResult = useQuery<TData, TVariables>(
    query,
    queryOptions as ApolloUseQuery.Options<NoInfer<TData>, NoInfer<TVariables>>
  )
  const { data, fetchMore, loading } = queryResult
  const { hasNextPage, endCursor } = getPageInfo(data, property)

  // when use refetch need reset isLoadingMore
  // when change variables need reset isLoadingMore
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const loadMore = useCallback(async () => {
    if (isLoadingMore || loading || !hasNextPage) {
      return
    }
    setIsLoadingMore(true)

    return fetchMore({
      variables: {
        cursor: endCursor
      } as Partial<NoInfer<TVariables>>
    }).finally(() => setIsLoadingMore(false))
  }, [isLoadingMore, loading, hasNextPage, fetchMore, endCursor])

  return {
    ...queryResult,
    loadMore,
    isLoadingMore,
    hasNextPage
  }
}
